import { useForm, FormProvider } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useTranslations } from "use-intl";
import { FormCheckbox, FormInput, FormSelect } from "@/components/form/rhf";
import { Button } from "@/components/ui/button";
import { useUpdateStep } from "@/hooks/api/mutations/workflows.mutations";
import { useConnectors } from "@/hooks/api/queries/connectors.queries";
import type { WorkflowStep } from "@/types/workflow.types";

const optionalNumber = z.preprocess(
  (v) => (v === "" || v == null || Number.isNaN(v) ? undefined : Number(v)),
  z.number().int().positive().optional(),
);

const schema = z.object({
  connectorId: z.string().optional(),
  tableName: z.string().min(1, "Table required"),
  databaseName: z.string().optional(),
  mode: z.enum(["append", "replace", "upsert"]),
  autoCreateTable: z.boolean(),
  primaryKeys: z.string().optional(),
  updateColumns: z.string().optional(),
  batchSize: optionalNumber,
  d1BatchSize: optionalNumber,
  d1UpsertStrategy: z.enum(["replace", "ignore"]),
  parallelEnabled: z.boolean(),
  parallelWorkers: optionalNumber,
});

type FormValues = z.infer<typeof schema>;
type FormInput = z.input<typeof schema>;

function toList(raw?: string) {
  return (raw ?? "").split(",").map((item) => item.trim()).filter(Boolean);
}

function toFormValues(step: WorkflowStep): FormInput {
  const config = step.config;
  return {
    connectorId: step.connectorId ?? "",
    tableName: String(config.tableName ?? ""),
    databaseName: String(config.databaseName ?? ""),
    mode: (["append", "replace", "upsert"].includes(String(config.mode)) ? config.mode : "append") as FormValues["mode"],
    autoCreateTable: config.autoCreateTable === true,
    primaryKeys: Array.isArray(config.primaryKeys) ? (config.primaryKeys as string[]).join(", ") : "",
    updateColumns: Array.isArray(config.updateColumns) ? (config.updateColumns as string[]).join(", ") : "",
    batchSize: typeof config.batchSize === "number" ? config.batchSize : undefined,
    d1BatchSize: typeof config.d1BatchSize === "number" ? config.d1BatchSize : undefined,
    d1UpsertStrategy: config.d1UpsertStrategy === "ignore" ? "ignore" : "replace",
    parallelEnabled: config.parallelEnabled === true,
    parallelWorkers: typeof config.parallelWorkers === "number" ? config.parallelWorkers : undefined,
  };
}

function toPayload(step: WorkflowStep, values: FormValues): Record<string, unknown> {
  return {
    ...step.config,
    tableName: values.tableName,
    databaseName: values.databaseName || undefined,
    mode: values.mode,
    autoCreateTable: values.autoCreateTable,
    primaryKeys: toList(values.primaryKeys),
    updateColumns: toList(values.updateColumns),
    batchSize: values.batchSize,
    d1BatchSize: values.d1BatchSize,
    d1UpsertStrategy: values.d1UpsertStrategy,
    parallelEnabled: values.parallelEnabled,
    parallelWorkers: values.parallelWorkers,
  };
}

interface LoadConfigFormProps {
  step: WorkflowStep;
  workflowId: string;
  onSuccess?: () => void;
}

export function LoadConfigForm({ step, workflowId, onSuccess }: LoadConfigFormProps) {
  const t = useTranslations("workflows");
  const updateStep = useUpdateStep();
  const { data: connectors } = useConnectors({ limit: 200, type: "credential" });
  const connectorOptions = [
    { value: "", label: t("drawer.placeholder.keep_connector") },
    ...(connectors?.data ?? []).map((connector) => ({
      value: connector.id,
      label: `${connector.name}${connector.subtype ? ` (${connector.subtype})` : ""}`,
    })),
  ];
  const methods = useForm<FormInput, unknown, FormValues>({
    resolver: zodResolver(schema),
    defaultValues: toFormValues(step),
  });
  const { handleSubmit, formState: { isSubmitting } } = methods;

  const onSubmit = handleSubmit(async (values) => {
    await updateStep.mutateAsync({
      workflowId,
      stepId: step.id,
      config: toPayload(step, values),
      connectorId: values.connectorId || undefined,
    });
    onSuccess?.();
  });

  return (
    <FormProvider {...methods}>
      <form onSubmit={onSubmit} className="flex flex-col gap-4">
        <FormSelect name="connectorId" label={t("drawer.field.connector")} options={connectorOptions} />

        <div className="grid grid-cols-2 gap-3">
          <FormInput name="tableName" label={t("drawer.field.table_name")} required />
          <FormInput name="databaseName" label={t("drawer.field.database_name")} />
        </div>

        <FormSelect
          name="mode"
          label={t("drawer.field.load_mode")}
          options={[
            { value: "append", label: "append" },
            { value: "replace", label: "replace" },
            { value: "upsert", label: "upsert" },
          ]}
        />

        <FormCheckbox name="autoCreateTable" label={t("drawer.field.auto_create_table")} />

        <FormInput name="primaryKeys" label={t("drawer.field.primary_keys")} hint={t("drawer.hint.comma_list")} />
        <FormInput name="updateColumns" label={t("drawer.field.update_columns")} hint={t("drawer.hint.comma_list")} />

        <div className="grid grid-cols-2 gap-3">
          <FormInput name="batchSize" type="number" label={t("drawer.field.batch_size")} />
          <FormInput name="d1BatchSize" type="number" label={t("drawer.field.d1_batch_size")} />
        </div>

        <FormSelect
          name="d1UpsertStrategy"
          label={t("drawer.field.d1_upsert_strategy")}
          options={[
            { value: "replace", label: "replace" },
            { value: "ignore", label: "ignore" },
          ]}
        />
        <FormCheckbox name="parallelEnabled" label={t("drawer.field.parallel_enabled")} />
        {methods.watch("parallelEnabled") && (
          <FormInput name="parallelWorkers" type="number" label={t("drawer.field.parallel_workers")} />
        )}

        {updateStep.isError && <p className="text-xs text-[var(--destructive)]">{t("drawer.save_error")}</p>}
        <Button type="submit" variant="primary" size="sm" disabled={isSubmitting || updateStep.isPending} className="w-full">
          {isSubmitting || updateStep.isPending ? t("drawer.saving") : t("drawer.save")}
        </Button>
      </form>
    </FormProvider>
  );
}

export default LoadConfigForm;
